import React from "react";
import { ExpressionConfiguration } from "../rule-engine-core/Expressions/ExpressionConfiguration";
import styled from "styled-components";

import { ExpressionReactContext, ExpressionRenderType } from "./Structure/ExpressionReactContext";

import { ExpressionValueType } from "../rule-engine-core/Expressions/ExpressionValueType";
import { ExpressionValueDialog } from "./Utilities/ExpressionValueDialog";
import { ObserveState } from "../CommonUI/StateManagement/ObserveState";
import { ExpressionValue } from "../rule-engine-core/Expressions/ExpressionValue";

import { TestContext } from "../CommonUI/Testing/TestContext";
import { TestResult } from "../CommonUI/Testing/TestResult";
import { ValidationInformation } from "../Rule/DesignUI/ValidationInformation";
import { RenderExpressionRule } from "./Utilities/RegisterExpressionRuleEditor";
import { Box } from "@mui/material";
import { AppGlueCoreInit } from "../AppGlueCoreInit";
import { UIStyleConstants } from "../rule-engine-core/Common/UIStyleConstants";

const WholeExpression = styled.div<{ renderType: ExpressionRenderType }>`
    height: 100%;
    ${(props) =>
        props.renderType === ExpressionRenderType.FULL
            ? `
                font-family:    Mulish;
                font-size:      28px;
                color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
            `
            : `
                
                font-size: 12px;
            `}
`;

const Expression = styled.div<{ renderType: ExpressionRenderType }>`
    width: 100%;

    ${(props) =>
        props.renderType === ExpressionRenderType.FULL
            ? `
                min-height: 200px;
                border: 2px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};
                border-radius: 5px;
                padding-top: 20px;
                padding-right: 15px;
                padding-bottom: 20px;
                padding-left: 15px;
            `
            : ``}
`;

const Expects = styled.div<{ hasError: boolean }>`
    padding-top: 15px;
    padding-right: 5px;
    padding-bottom: 15px;
    padding-left: 5px;
    clear: left;

    font-size: 12px;

    ${(props) => (props.hasError ? "margin-bottom: 30px" : "")}
`;

export interface XExpressionEditorData {
    expression: ExpressionConfiguration;
    openFirstTimeWhenEmptyExpression?: boolean;
    onUpdate?: () => void;
}

export class ExpressionBuilder extends React.Component<XExpressionEditorData, {}> {
    static contextType = ExpressionReactContext;
    context!: React.ContextType<typeof ExpressionReactContext>;

    constructor(props: XExpressionEditorData) {
        super(props);

        AppGlueCoreInit.init();
    }

    override componentDidMount() {
        this.props.expression.editContext!.onExpressionUpdate = this.props.onUpdate;
        if (
            this.props.openFirstTimeWhenEmptyExpression &&
            (this.props.expression.valueType === ExpressionValueType.UNSET ||
                (this.props.expression.valueType === ExpressionValueType.SUBEXPRESSION &&
                    !this.props.expression.expressionRoot.subExpression))
        ) {
            this.props.expression.editContext!.setSelection(this.props.expression.expressionRoot?._id);
        }
    }

    renderEditDialog() {
        return (
            <TestContext.Consumer>
                {(value) => {
                    if (value) return null;

                    if (this.context.renderType !== ExpressionRenderType.FULL) {
                        return null;
                    }

                    return (
                        <ObserveState
                            listenTo={this.props.expression.editContext}
                            properties={["selected"]}
                            control={() => {
                                const selected = this.props.expression.editContext?.getSelection();
                                if (!selected) return null;
                                const expressionValue = this.props.expression.editContext!.getElement(
                                    selected,
                                ) as ExpressionValue;
                                if (!expressionValue) return null;

                                return (
                                    <ExpressionValueDialog
                                        expressionValue={expressionValue}
                                        onClose={() => this.props.expression.editContext.clearSelection()}
                                    />
                                );
                            }}
                        />
                    );
                }}
            </TestContext.Consumer>
        );
    }

    render() {
        return (
            <WholeExpression
                renderType={this.context.renderType}
                data-testid="XExpressionEditor"
                id="XExpressionEditor"
                className="XExpressionEditor-root"
            >
                <Expression
                    className="XExpressionEditor"
                    renderType={this.context.renderType}
                >
                    {RenderExpressionRule(this.props.expression.rootExpression)}
                </Expression>

                {this.context.renderType === ExpressionRenderType.FULL && (
                    <TestResult executableId={this.props.expression._id} />
                )}

                {this.renderEditDialog()}

                {this.context.renderType === ExpressionRenderType.FULL && (
                    <Box>
                        <ObserveState
                            listenTo={this.props.expression.editContext}
                            control={() => <ValidationInformation provider={this.props.expression} />}
                        />
                        <Expects hasError={this.props.expression.getDesignValidationIssues().length > 0}>
                            <i>
                                expects <u>{this.props.expression.getRuleResult().type} </u>
                            </i>
                            &nbsp;&nbsp;result
                        </Expects>
                    </Box>
                )}
            </WholeExpression>
        );
    }
}
